import { computed } from 'vue'
import type { Schemas } from '#shopware'

// Static data structure (from config.images.value)
export interface DetailGalleryImageStatic {
    media: Schemas['Media']
    mediaId: string
    mediaUrl: string
}

// Mapped data structure (from data.images with product.media mapping)
export interface DetailGalleryImageMapped {
    mediaId: string
    image: {
        mediaId: string
        url: string | null
        newTab: boolean | null
        media: Schemas['Media']
    }
}

// Product media structure (from product.media via useProduct context)
export interface DetailGalleryImageProduct {
    mediaId: string
    position: number
    media: Schemas['Media']
}

// Union type to handle both structures
export type DetailGalleryImage = DetailGalleryImageStatic | DetailGalleryImageMapped | DetailGalleryImageProduct

export interface CmsElementDetailGallery {
    versionId: string
    type: string
    slot: string
    config: {
        images: {
            value: DetailGalleryImageStatic[] | string // string when mapped (e.g., "product.media")
            source: 'static' | 'mapped'
        }
    }
    data?: {
        images?: {
            [key: string]: DetailGalleryImage
        }
        product?: Schemas['Product']
        configuratorSettings?: Schemas['PropertyGroup'][]
    }
}

// Type guard to check if image is mapped structure
function isMappedImage(image: DetailGalleryImage): image is DetailGalleryImageMapped {
    return 'image' in image && typeof image.image === 'object' && 'media' in image.image
}

// Type guard to check if image is product media structure
function isProductImage(image: DetailGalleryImage): image is DetailGalleryImageProduct {
    return 'position' in image && 'media' in image && !('image' in image)
}

export function useDetailGallery(content: CmsElementDetailGallery) {
    // Inject product from page-level context (provided by FrontendDetailPage via useProduct)
    const { product } = useProduct()

    const images = computed<DetailGalleryImage[]>(() => {
        // First try to get images from product.media (preserves position ordering)
        const productMedia = product.value?.media
        if (productMedia && productMedia.length > 0) {
            return [...productMedia]
                .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)) as DetailGalleryImageProduct[]
        }

        // Fallback to data.images (CMS resolved)
        if (content?.data?.images) {
            return Object.values(content.data.images)
        }

        // Fallback to config value (only for static source)
        if (content?.config?.images?.source === 'static' && Array.isArray(content?.config?.images?.value)) {
            return content.config.images.value
        }

        return []
    })

    const getImageUrl = (image: DetailGalleryImage) => {
        if (isProductImage(image)) {
            return image.media?.url || ''
        }
        if (isMappedImage(image)) {
            // Mapped structure: image.image.media.url
            return image.image?.media?.url || ''
        }
        // Static structure: image.media.url or image.mediaUrl
        return (image as DetailGalleryImageStatic).media?.url || (image as DetailGalleryImageStatic).mediaUrl || ''
    }

    const getImageAlt = (image: DetailGalleryImage) => {
        if (isProductImage(image)) {
            return image.media?.alt || ''
        }
        if (isMappedImage(image)) {
            return image.image?.media?.alt || ''
        }
        return (image as DetailGalleryImageStatic).media?.alt || ''
    }

    const getImageWidth = (image: DetailGalleryImage) => {
        if (isProductImage(image)) {
            return image.media?.metaData?.width || 0
        }
        if (isMappedImage(image)) {
            return image.image?.media?.metaData?.width || 0
        }
        return (image as DetailGalleryImageStatic).media?.metaData?.width || 0
    }

    const getImageHeight = (image: DetailGalleryImage) => {
        if (isProductImage(image)) {
            return image.media?.metaData?.height || 0
        }
        if (isMappedImage(image)) {
            return image.image?.media?.metaData?.height || 0
        }
        return (image as DetailGalleryImageStatic).media?.metaData?.height || 0
    }

    return {
        images,
        getImageUrl,
        getImageAlt,
        getImageWidth,
        getImageHeight
    }
}